import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Prompt } from '../../../types/backend';
import { slugifyPromptName } from '../utils/promptUtils';
import { usePromptManagement, PromptFormData } from './usePromptManagement';
import { usePromptService } from '../services/promptService';

interface UsePromptFormOptions {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

// One key for every per-version content read (DRAFT included), so selecting a
// prompt and then viewing its versions share a single cache entry.
export const promptVersionKey = (
  promptId: string,
  version: string,
  owner: string,
  customerId: string | undefined,
) => ['prompts', 'version', promptId, version, owner, customerId ?? null] as const;

export function usePromptForm({ customerId, isOpen, onClose }: UsePromptFormOptions) {
  const [showGlobal, setShowGlobal] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [justPublished, setJustPublished] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<string | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<string | null>(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const queryClient = useQueryClient();

  const promptService = usePromptService();
  const { promptService: managementPromptService, entityManagement } = usePromptManagement(customerId, isOpen);

  const {
    entities: allPrompts,
    loading,
    error,
    selectedEntity: selectedPrompt,
    formData,
    saving,
    isNew: isNewPrompt,
    showDeleteConfirm,
    successMessage,
    loadEntities,
    handleSelect: handleSelectPrompt,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    setError,
    setSuccessMessage,
    isFormValid,
    hasChanges,
    fieldErrors,
    hasAttemptedSave,
  } = entityManagement;

  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  const fetchAndSelect = useCallback(async (prompt: Prompt) => {
    if (prompt.content) {
      handleSelectPrompt(prompt);
      return;
    }
    try {
      // Read through the cache so this shares a key with the version picker, but
      // staleTime 0 — DRAFT is mutable and this hook has no write path that busts it.
      // Inheriting the client's 30s default would serve a pre-save body on re-select.
      const draft = await queryClient.fetchQuery({
        queryKey: promptVersionKey(prompt.id, 'DRAFT', prompt.owner, customerId),
        queryFn: () => managementPromptService.getPromptVersion(
          prompt.id, 'DRAFT', prompt.owner,
          prompt.owner === 'customer' ? customerId : undefined
        ),
        staleTime: 0,
      });
      handleSelectPrompt({
        ...prompt,
        content: draft.content || '',
        availableVersions: draft.availableVersions || [],
        variables: draft.variables || [],
      });
    } catch {
      handleSelectPrompt({ ...prompt, content: '' });
    }
  }, [handleSelectPrompt, managementPromptService, customerId, queryClient]);

  const handleSelectPromptWithReset = useCallback((prompt: Prompt) => {
    setJustPublished(false);
    fetchAndSelect(prompt);
  }, [fetchAndSelect]);

  const selectedPromptRef = useRef<Prompt | null>(null);
  selectedPromptRef.current = selectedPrompt;

  const autoFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedPrompt && !selectedPrompt.content && autoFetchedRef.current !== selectedPrompt.id) {
      autoFetchedRef.current = selectedPrompt.id;
      fetchAndSelect(selectedPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrompt?.id]);

  const handleAddNewWithReset = useCallback(() => {
    handleAddNew();
  }, [handleAddNew]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, setSuccessMessage]);

  useEffect(() => {
    if (isNewPrompt && formData.title) {
      const slug = slugifyPromptName(formData.title, formData.type);
      if (slug) handleChangeRef.current('name', slug);
    }
  }, [isNewPrompt, formData.title, formData.type]);

  const displayedPrompts = useMemo(() => {
    const customer = allPrompts.filter(p => p.owner === 'customer' && p.customerId === customerId);
    const global = allPrompts.filter(p => p.owner === 'global');
    customer.sort((a, b) => a.title.localeCompare(b.title));
    global.sort((a, b) => a.title.localeCompare(b.title));
    return showGlobal ? [...customer, ...global] : customer;
  }, [allPrompts, showGlobal, customerId]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const p of displayedPrompts) {
      for (const t of p.tags) tagSet.add(t);
    }
    return Array.from(tagSet).sort();
  }, [displayedPrompts]);

  const handlePublish = useCallback(async () => {
    if (!selectedPrompt) return;
    setPublishing(true);
    try {
      await managementPromptService.publishPrompt(
        selectedPrompt.id,
        selectedPrompt.owner,
        customerId
      );
      await loadEntities();
      setJustPublished(true);
      setSuccessMessage('Published successfully');
    } catch (err) {
      setError(`Failed to publish: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setPublishing(false);
    }
  }, [selectedPrompt, managementPromptService, customerId, loadEntities, setSuccessMessage, setError]);

  useEffect(() => {
    setSettingsExpanded(isNewPrompt);
  }, [isNewPrompt, selectedPrompt]);

  useEffect(() => {
    setViewingVersion(null);
    // Also clears a delete still in flight on the prompt being left: deletingVersion
    // is matched by identifier alone, so it would disable this prompt's version control
    // and spin against a same-numbered version here.
    setDeletingVersion(null);
  }, [selectedPrompt]);

  // The content of a published version being previewed. DRAFT is not read here —
  // it is already on the selected prompt (seeded by fetchAndSelect above).
  const versionQuery = useQuery({
    queryKey: selectedPrompt && viewingVersion
      ? promptVersionKey(selectedPrompt.id, viewingVersion, selectedPrompt.owner, customerId)
      : ['prompts', 'version', 'none'],
    queryFn: () => {
      if (!selectedPrompt || !viewingVersion) {
        throw new Error('Cannot load a prompt version without a selected prompt and version');
      }
      return promptService.getPromptVersion(
        selectedPrompt.id, viewingVersion, selectedPrompt.owner,
        selectedPrompt.owner === 'customer' ? customerId : undefined
      );
    },
    enabled: !!selectedPrompt && !!viewingVersion,
    // A published version is immutable once minted — switching back to it costs nothing.
    staleTime: Infinity,
  });

  const viewingContent = versionQuery.data?.content ?? '';
  const versionLoading = versionQuery.isLoading;

  // A failed version read drops the preview back to DRAFT and reports it — the
  // editor must never silently show empty content as if it were the version.
  const failedVersion = versionQuery.isError ? viewingVersion : null;
  useEffect(() => {
    if (failedVersion) {
      setError(`Failed to load version ${failedVersion}`);
      setViewingVersion(null);
    }
  }, [failedVersion, setError]);

  const handleVersionChange = useCallback((version: string) => {
    if (!selectedPrompt) return;
    setViewingVersion(version === 'DRAFT' ? null : version);
  }, [selectedPrompt]);

  // Bedrock caps published versions per prompt, so freeing a slot is what lets an
  // at-the-limit prompt be published again. The backend refuses the live version and
  // DRAFT; the editor drops back to DRAFT because the version it was showing is gone.
  const handleDeleteVersion = useCallback(async (version: string) => {
    if (!selectedPrompt) return;
    setDeletingVersion(version);
    try {
      await managementPromptService.deletePromptVersion(
        selectedPrompt.id, version, selectedPrompt.owner,
        selectedPrompt.owner === 'customer' ? customerId : undefined
      );
      // That version is gone: drop its cached body so it can never be served again.
      queryClient.removeQueries({
        queryKey: promptVersionKey(selectedPrompt.id, version, selectedPrompt.owner, customerId),
      });
      // Re-read DRAFT for the shortened version list. Reloading the prompt list instead
      // would re-seed the editor from the list shape, which carries no content and no
      // availableVersions — blanking the editor. Deleting a non-live version does not
      // change anything the list holds, so there is nothing there to refresh.
      //
      // Every bit of view state below belongs to the prompt this delete started on, so
      // none of it may be touched once the operator has moved to a different prompt:
      // re-selecting would replace their form, and clearing viewingVersion would flip
      // the version they are reading back to Draft.
      if (selectedPromptRef.current?.id === selectedPrompt.id) {
        setViewingVersion(null);
        await fetchAndSelect({ ...selectedPrompt, content: '' });
      }
      setSuccessMessage(`Deleted version ${version}`);
    } catch (err) {
      setError(`Failed to delete version ${version}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingVersion(null);
    }
  }, [selectedPrompt, managementPromptService, customerId, fetchAndSelect, queryClient, setSuccessMessage, setError]);

  const handleContentChange = useCallback(<K extends keyof PromptFormData>(field: K, value: PromptFormData[K]) => {
    if (justPublished) setJustPublished(false);
    handleChange(field, value);
  }, [justPublished, handleChange]);

  return {
    // Entity data
    loading,
    error,
    selectedPrompt,
    formData,
    saving,
    isNewPrompt,
    showDeleteConfirm,
    successMessage,
    displayedPrompts,
    allTags,
    customerId,

    // UI state
    showGlobal,
    setShowGlobal,
    publishing,
    hasAttemptedSave,
    justPublished,
    viewingVersion,
    viewingContent,
    versionLoading,
    deletingVersion,
    settingsExpanded,
    setSettingsExpanded,
    isViewingHistory: viewingVersion !== null,

    // Validity
    isFormValid,
    hasChanges,
    fieldErrors,

    // Handlers
    handleSelect: handleSelectPromptWithReset,
    handleAddNew: handleAddNewWithReset,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange: handleContentChange,
    handleClose,
    handlePublish,
    handleVersionChange,
    handleDeleteVersion,
    setError,
    setSuccessMessage,
  };
}
